import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import socketClient from "socket.io-client";
import { getParticipantDataForCandidate, getUserDataByGuid } from "./userJson";
// eslint-disable-next-line
import { getInterviewDetails } from "services/api";
import { haveToUseDeepgramForAiInterview } from "utils/constant";
import { effectsProcessor } from "utils/EffectsProcessor";

const {
  REACT_APP_AI_IN_SOCKET_URL,
  REACT_APP_HUMAN_IN_SOCKET_URL,
  REACT_APP_CLOUD_FUNCTIONS_URL,
} = process.env;

export const CommonContext = createContext();

export const CommonProvider = ({ children }) => {
  // ---------- stable refs ----------
  const isSavedCurrentUserData = useRef(false);
  const unsubscribers = useRef([]); // socket listener disposers

  const { interview_id } = useParams();
  const [room_id, setRoom_id] = useState("");
  const socketRef = useRef(null);
  const peers = useRef({}); // userId -> SimplePeer
  const peerRecreateTime = useRef({}); // userId -> last recreate timestamp (ms)
  const joinSoundRef = useRef(null);

  // Local media
  const localStream = useRef(null);
  const rawCameraStream = useRef(null); // raw (unprocessed) camera stream fed to EffectsProcessor
  const cameraVideoTrack = useRef(null);
  const screenVideoTrack = useRef(null);
  const screenStreamRef = useRef(null);
  const canvasRecorderRef = useRef(null);

  // Preferred device IDs — set from join page, persisted in sessionStorage
  const selectedCameraIdRef = useRef(
    sessionStorage.getItem("bizro_camId") || "",
  );
  const selectedMicIdRef = useRef(sessionStorage.getItem("bizro_micId") || "");

  // UI state
  const [userName, setUserName] = useState("");
  const userNameRef = useRef(""); // stable ref for use inside handlers
  const [userOwnData, setUserOwnData] = useState({
    guid: "",
    username: "",
    fname: "",
    lname: "",
    email: "",
    position: "",
    isCandidate: null,
    socketId: "",
    colorCode: "#848484",
    isCameraOn: true,
    isAudioOn: true,
    isJoin: false,
    userNameValue: "",
  });

  let userData = { username: "", userId: "" };

  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [isInMeetingRoom, setIsInMeetingRoom] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const audioEnabledRef = useRef(true); // stable ref — avoids stale closure in toggleAudio
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isInterviewEnded, setIsInterviewEnded] = useState(false);
  const [typeOfInterview, setTypeOfInterview] = useState("");

  let participantData = [];
  const [participantList, setParticipantList] = useState([]);
  const [participantOfMeeting, setParticipantOfMeeting] = useState([]);
  const participantOfMeetingRef = useRef([]);
  useEffect(() => {
    participantOfMeetingRef.current = participantOfMeeting;
  }, [participantOfMeeting]);

  const [aiAgentDetail, setAiAgentDetail] = useState({});
  const [note, setNote] = useState("");
  const idRequestForNotes = useRef(false);
  const [feedbackGuid, setFeedbackGuid] = useState("");

  const [isScreenSharedInMeeting, setIsScreenSharedInMeeting] = useState({
    isShared: false,
    isSelfShared: false,
  });

  const [screenSharedStreams, setScreenSharedStreams] = useState({});

  const [participateIdToMessage, setParticipateIdToMessage] = useState("");
  const [groupMessagesData, setGroupMessagesData] = useState([]);
  const [privateChatMessages, setPrivateChatMessages] = useState([]);
  const [generalChatMessages, setGeneralChatMessages] = useState([]);
  const [isComeNewMessage, setIsComeNewMessage] = useState("");
  const [microphoneError, setMicrophoneError] = useState(false);

  const [interviewDetails, setInterviewDetails] = useState({});
  const [currentUserJoined, setCurrentUserJoined] = useState({
    username: "",
    userId: "",
  });

  // background effect for blur / none
  const [backgroundEffect, setBackgroundEffect] = useState("none"); // "none" | "blur"

  const queryParams = new URLSearchParams(window.location.search);
  const cid = queryParams.get("cid");

  const iceServersRef = useRef(null);
  const room_idRef = useRef(""); // stable ref for room_id inside handlers

  const MEDIA_CONSTRAINTS = {
    video: {
      ...(selectedCameraIdRef.current
        ? { deviceId: { exact: selectedCameraIdRef.current } }
        : {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30, max: 30 },
            facingMode: "user",
          }),
    },
    audio: {
      ...(selectedMicIdRef.current
        ? { deviceId: { exact: selectedMicIdRef.current } }
        : {}),
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      autoGainControl: { ideal: true },
    },
  };

  // Keep room_idRef in sync
  useEffect(() => {
    room_idRef.current = room_id;
  }, [room_id]);

  // Keep userNameRef in sync
  useEffect(() => {
    userNameRef.current = userName;
  }, [userName]);

  const safeRemoveAllSocketListeners = () => {
    if (!socketRef.current) return;
    unsubscribers.current.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
    unsubscribers.current = [];
    socketRef.current.removeAllListeners();
  };

  const attachSocket = (serverUrl) => {
    if (socketRef.current) return;

    socketRef.current = socketClient(serverUrl, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
      timeout: 20000,
      withCredentials: true,
      extraHeaders: { "ngrok-skip-browser-warning": "true" },
    });

    // IMPORTANT: This must be registered here, NOT in a useEffect.
    // The network useEffect runs with [] at mount when socketRef is still null,
    // so any socket.on() call inside it is silently skipped.
    // socket.io fires "connect" on both the initial connection AND every
    // reconnect (network switch, mobile data, power outage).
    // isInMeetingRoomRef guards against running on the initial connection.
    socketRef.current.on("connect", () => {
      if (!isInMeetingRoomRef.current) return;
      if (!room_idRef.current || !userOwnDataRef.current?.guid) return;

      console.info("[Socket] reconnected — re-joining room");

      // Destroy stale peers — our socket ID changed, old peers are zombies
      Object.keys(peers.current).forEach((uid) => destroyPeer(uid));

      // Allow onUserJoined to refresh our userOwnData with the new socket ID
      isSavedCurrentUserData.current = false;

      // Re-join room. Server broadcasts user_joined to all other participants
      // with our new socket ID. They create peers as initiator toward us.
      // We respond via onSignal as non-initiator — no offer/offer collision.
      socketRef.current.emit("join", {
        room_id: room_idRef.current,
        ...userOwnDataRef.current,
      });

      console.info("[Socket] join emitted — waiting for peer offers");
    });
  };

  const enrichUserWithNameValue = (u) => {
    if (!u) return u;
    const fullName = [u.fname || u.firstName, u.lname || u.lastName]
      .filter(Boolean)
      .join(" ");
    return {
      ...u,
      userNameValue: u.userNameValue || fullName || u.username || "",
    };
  };

  const fetchIceServers = async () => {
    try {
      const res = await fetch(
        REACT_APP_CLOUD_FUNCTIONS_URL + "/ice_turn_config",
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!res.ok) {
        throw new Error("Failed to fetch TURN credentials");
      }

      const turn = await res.json();

      iceServersRef.current = [
        { urls: ["stun:stun.l.google.com:19302"] },
        {
          urls: turn.urls,
          username: turn.username,
          credential: turn.credential,
        },
      ];
      console.info(
        "[ICE] TURN servers loaded:",
        JSON.stringify(iceServersRef.current),
      );
    } catch (err) {
      console.error("TURN credential fetch failed:", err);

      // Fallback: STUN only (meeting may still work in open networks)
      iceServersRef.current = [{ urls: ["stun:stun.l.google.com:19302"] }];
    }
  };

  const fetchInterviewDetails = async () => {
    await fetchIceServers();

    await getInterviewDetails(interview_id)
      .then(async (response) => {
        if (response.data.status === "ERROR") {
          setIsValidUrl(false);
        }
        let interviewerAPIJson = response.data.data;
        if (
          interviewerAPIJson?.status === "E" ||
          interviewerAPIJson?.status === "C"
        ) {
          setIsValidUrl(false);
        }
        setInterviewDetails({
          ...interviewerAPIJson,
          candidateDocument: interviewerAPIJson?.candidateDocument?.find(
            (doc) => doc.documentTypeDisplayName === "Resume",
          ),
        });

        setTypeOfInterview(interviewerAPIJson.interviewType);

        const SERVER =
          interviewerAPIJson.interviewType === "AI"
            ? REACT_APP_AI_IN_SOCKET_URL
            : REACT_APP_HUMAN_IN_SOCKET_URL;

        if (
          haveToUseDeepgramForAiInterview ||
          interviewerAPIJson.interviewType !== "AI"
        ) {
          attachSocket(SERVER);
        }
        setAiAgentDetail(interviewerAPIJson.aiAgent);

        if (cid) {
          setIsLoggedIn(true);
          setRoom_id(interview_id);
          const isValidInterviewer = interviewerAPIJson.interviewerName.some(
            (interviewer) => interviewer.guid === cid,
          );
          setIsValidUrl(isValidInterviewer);
          const meetingUser = getUserDataByGuid(
            cid,
            interviewerAPIJson.jobDetails.displayName,
            interviewerAPIJson.interviewerName,
            interviewerAPIJson.candidateDetails,
            interviewerAPIJson.candidateApplicationGuid,
          );
          if (meetingUser) {
            // ensure every participant has userNameValue
            setParticipantOfMeeting(
              meetingUser.participantOfMeeting.map(enrichUserWithNameValue),
            );

            const enrichedUser = enrichUserWithNameValue(meetingUser.userData);

            setUserOwnData((previousData) => ({
              ...previousData,
              interviewType: interviewerAPIJson.interviewType,
              interviewQuestions: interviewerAPIJson.interviewQuestions,
              candidateApplicationGuid:
                interviewerAPIJson.candidateApplicationGuid,
              ...enrichedUser,
            }));
            setUserName(meetingUser.userData.username);
            setRoom_id(interview_id);
            setIsLoggedIn(true);
          }
        } else {
          const meetingUser = getParticipantDataForCandidate(
            interviewerAPIJson.jobDetails.displayName,
            interviewerAPIJson.interviewerName,
            interviewerAPIJson.candidateDetails,
            interviewerAPIJson.candidateApplicationGuid,
          );

          if (meetingUser) {
            setParticipantOfMeeting(
              meetingUser.participantOfMeeting.map(enrichUserWithNameValue),
            );

            const enrichedUser = enrichUserWithNameValue(meetingUser.userData);

            setUserOwnData((previousData) => ({
              ...previousData,
              interviewType: interviewerAPIJson.interviewType,
              interviewQuestions: interviewerAPIJson.interviewQuestions,
              candidateApplicationGuid:
                interviewerAPIJson.candidateApplicationGuid,
              ...enrichedUser,
            }));
            setRoom_id(interview_id);
          }
        }

        setIsInitialLoading(false);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchInterviewDetails();
    // eslint-disable-next-line
  }, [interview_id]);

  // Participant Join Sound
  useEffect(() => {
    try {
      joinSoundRef.current = new Audio("sounds/participant-join.mp3");
    } catch (e) {
      console.warn("Could not initialize join sound", e);
    }
  }, []);

  // ---------- media ----------

  // Returns video constraints sized to the current network quality tier.
  // Called at getUserMedia time so it reflects conditions when the call starts.
  const getAdaptiveVideoConstraints = () => {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    const etype = conn?.effectiveType;
    const dl = conn?.downlink;

    const isLow =
      etype === "slow-2g" ||
      etype === "2g" ||
      (dl !== undefined && dl < 0.5);
    const isMedium =
      etype === "3g" ||
      (dl !== undefined && dl >= 0.5 && dl < 1.5);

    if (isLow)
      return {
        width: { ideal: 320 },
        height: { ideal: 240 },
        frameRate: { ideal: 15, max: 15 },
      };
    if (isMedium)
      return {
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 20, max: 20 },
      };
    return {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30, max: 30 },
    };
  };

  // Returns the maxBitrate (bps) appropriate for the current network tier.
  const getAdaptiveBitrate = () => {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    const etype = conn?.effectiveType;
    const dl = conn?.downlink;
    const isCellular = conn?.type === "cellular";

    const isLow =
      etype === "slow-2g" ||
      etype === "2g" ||
      (dl !== undefined && dl < 0.5);
    const isMedium =
      etype === "3g" ||
      (dl !== undefined && dl >= 0.5 && dl < 1.5);

    if (isLow) return 200000;       // 200 kbps
    if (isMedium) return 500000;    // 500 kbps
    if (isCellular) return 800000;  // 4G cellular — high jitter, cap below WiFi
    return 1200000;                 // 1.2 Mbps (WiFi / ethernet)
  };

  const startCameraAudio = async () => {
    setMicrophoneError(false);
    stopMedia();

    const applyStream = (rawStream) => {
      rawCameraStream.current = rawStream;
      effectsProcessor.setRawStream(rawStream);
      localStream.current = rawStream;
      cameraVideoTrack.current = rawStream.getVideoTracks()[0] || null;
      setIsCameraOn(!!cameraVideoTrack.current);
      const initAudio = rawStream.getAudioTracks()[0]?.enabled ?? true;
      setAudioEnabled(initAudio);
      audioEnabledRef.current = initAudio;

      // Push tracks to any existing peers that were created before this stream
      // was ready (e.g. peer created while camera was still initialising).
      // Uses replaceTrack if a sender already exists, addTrack if not.
      const tracks = rawStream.getTracks();
      Object.values(peers.current).forEach((peer) => {
        if (!peer || peer.destroyed) return;
        const senders = peer._pc?.getSenders() || [];
        tracks.forEach((track) => {
          const existing = senders.find((s) => s.track?.kind === track.kind);
          try {
            if (existing) {
              existing.replaceTrack(track);
            } else {
              // Must go through SimplePeer's own addTrack, not peer._pc.addTrack
              // directly: only the wrapper calls _needsNegotiation() to trigger a
              // renegotiation offer. The vendored simple-peer bundle never listens
              // for the native RTCPeerConnection "negotiationneeded" event, so
              // adding a track straight to _pc silently never reaches the remote
              // peer's SDP.
              peer.addTrack?.(track, rawStream);
            }
          } catch (e) {
            console.warn("[applyStream] track push failed", e);
          }
        });
      });
    };

    // Attempt 1: adaptive video resolution + saved device IDs
    try {
      const adaptiveVideo = getAdaptiveVideoConstraints();
      const adaptiveConstraints = {
        video: {
          ...(selectedCameraIdRef.current
            ? { deviceId: { exact: selectedCameraIdRef.current } }
            : { facingMode: "user" }),
          ...adaptiveVideo,
        },
        audio: MEDIA_CONSTRAINTS.audio,
      };
      applyStream(await navigator.mediaDevices.getUserMedia(adaptiveConstraints));
      return;
    } catch (err) {
      console.warn("getUserMedia attempt 1 failed:", err?.name, err?.message);

      // NotAllowedError / PermissionDeniedError = browser genuinely blocked
      // Show the permission screen immediately, no point retrying
      if (
        err?.name === "NotAllowedError" ||
        err?.name === "PermissionDeniedError"
      ) {
        setMicrophoneError(true);
        return;
      }

      // NotFoundError or OverconstrainedError with a saved device ID = stale
      // sessionStorage value (common on mobile where IDs change). Clear it and retry.
      if (
        err?.name === "NotFoundError" ||
        err?.name === "OverconstrainedError" ||
        err?.name === "ConstraintNotSatisfiedError"
      ) {
        if (selectedCameraIdRef.current || selectedMicIdRef.current) {
          console.info(
            "Clearing stale device IDs from sessionStorage, retrying…",
          );
          selectedCameraIdRef.current = "";
          selectedMicIdRef.current = "";
          sessionStorage.removeItem("bizro_camId");
          sessionStorage.removeItem("bizro_micId");
        }
      }
    }

    // Attempt 2: minimal constraints, no saved device IDs, mobile-safe resolution
    try {
      applyStream(
        await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: {
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
          },
        }),
      );
      return;
    } catch (err2) {
      console.warn("getUserMedia attempt 2 failed:", err2?.name, err2?.message);

      if (
        err2?.name === "NotAllowedError" ||
        err2?.name === "PermissionDeniedError"
      ) {
        setMicrophoneError(true);
        return;
      }
    }

    // Attempt 3: audio-only fallback (camera may be in use by another app)
    try {
      applyStream(
        await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true,
        }),
      );
      console.info("Camera unavailable — audio-only mode");
      return;
    } catch (err3) {
      console.error(
        "getUserMedia all attempts failed:",
        err3?.name,
        err3?.message,
      );
    }

    // Only show the permission error screen if we truly cannot get any media
    setMicrophoneError(true);
  };

  // ─── Device selector integration ──────────────────────────────────────────
  // Listen for bizro:switchCamera / bizro:switchMic events fired by the
  // join-page device selector. Persists selection to sessionStorage so it
  // survives the join, updates MEDIA_CONSTRAINTS refs, and replaces the live
  // track on all active peer connections without renegotiation.
  useEffect(() => {
    const onSwitchCamera = async (e) => {
      const { deviceId, track: newTrack } = e.detail;
      selectedCameraIdRef.current = deviceId;
      sessionStorage.setItem("bizro_camId", deviceId);

      if (!localStream.current || !newTrack) return;

      // Let EffectsProcessor know about the new raw camera stream
      // so it reads frames from the new device going forward
      const newRawStream = new MediaStream([
        newTrack,
        ...localStream.current.getAudioTracks(),
      ]);
      rawCameraStream.current = newRawStream;
      await effectsProcessor.replaceRawStream(newRawStream);

      // If processor is active (blur/virtual BG on), get the processed track; else use raw
      const activeTrack = effectsProcessor.isActive()
        ? effectsProcessor.getOutputStream()?.getVideoTracks()[0] || newTrack
        : newTrack;

      // Use replaceVideoTrackForPeers to swap + notify CameraFeed
      replaceVideoTrackForPeers(activeTrack);
    };

    const onSwitchMic = async (e) => {
      const { deviceId, track: newTrack } = e.detail;
      selectedMicIdRef.current = deviceId;
      sessionStorage.setItem("bizro_micId", deviceId);

      if (!localStream.current || !newTrack) return;

      // Apply the selected mute state to the new track immediately
      newTrack.enabled = audioEnabledRef.current;

      // Swap audio track in localStream
      localStream.current.getAudioTracks().forEach((t) => {
        t.stop();
        localStream.current.removeTrack(t);
      });
      localStream.current.addTrack(newTrack);

      // Also update rawCameraStream so toggleAudio and replaceVideoTrackForPeers
      // always reference the correct live mic track going forward
      if (rawCameraStream.current) {
        rawCameraStream.current.getAudioTracks().forEach((t) => {
          t.stop();
          rawCameraStream.current.removeTrack(t);
        });
        rawCameraStream.current.addTrack(newTrack);
      }

      // Replace sender on every active peer, or add one if this peer never had
      // an audio sender to begin with (e.g. the initial join raced ahead of
      // getUserMedia). Must use the SimplePeer wrapper's addTrack, not
      // peer._pc.addTrack, so a missing sender actually triggers renegotiation.
      Object.values(peers.current).forEach((peer) => {
        try {
          const sender = peer._pc
            ?.getSenders()
            .find((s) => s.track?.kind === "audio");
          if (sender) {
            sender.replaceTrack(newTrack);
          } else {
            peer.addTrack?.(newTrack, localStream.current);
          }
        } catch {}
      });
    };

    window.addEventListener("bizro:switchCamera", onSwitchCamera);
    window.addEventListener("bizro:switchMic", onSwitchMic);
    return () => {
      window.removeEventListener("bizro:switchCamera", onSwitchCamera);
      window.removeEventListener("bizro:switchMic", onSwitchMic);
    };
  }, []); // eslint-disable-line

  const audioHealthTimerRef = useRef(null);
  const userOwnDataRef = useRef(userOwnData);
  useEffect(() => {
    userOwnDataRef.current = userOwnData;
  }, [userOwnData]);

  const startAudioHealthCheck = useCallback(() => {
    if (audioHealthTimerRef.current) clearInterval(audioHealthTimerRef.current);

    audioHealthTimerRef.current = setInterval(async () => {
      if (!localStream.current) return;
      if (!userOwnDataRef.current.isAudioOn) return; // user intentionally muted

      const audioTracks = localStream.current.getAudioTracks();
      const hasDeadTrack = audioTracks.some(
        (t) => t.readyState === "ended" || t.muted,
      );

      if (audioTracks.length === 0 || hasDeadTrack) {
        console.warn(
          "[AudioHealth] Dead/missing audio track detected — recovering…",
        );
        try {
          const newStream = await navigator.mediaDevices.getUserMedia({
            audio: MEDIA_CONSTRAINTS.audio,
          });
          const newTrack = newStream.getAudioTracks()[0];
          if (!newTrack) return;

          // Remove old dead tracks, add fresh one
          audioTracks.forEach((t) => {
            try {
              t.stop();
            } catch {}
            localStream.current.removeTrack(t);
          });
          localStream.current.addTrack(newTrack);

          // Replace the sender on every active peer connection
          Object.values(peers.current).forEach((peer) => {
            try {
              const senders = peer._pc?.getSenders() || [];
              const audioSender = senders.find(
                (s) => s.track?.kind === "audio",
              );
              if (audioSender) {
                audioSender.replaceTrack(newTrack);
              } else {
                // Peer has no audio sender yet — add fresh track. Must go
                // through SimplePeer's own addTrack (not peer._pc.addTrack)
                // so it triggers a renegotiation offer, see the matching note
                // in applyStream() above.
                peer.addTrack?.(newTrack, localStream.current);
              }
            } catch (e) {
              console.warn("[AudioHealth] replaceTrack failed for peer:", e);
            }
          });

          console.info("[AudioHealth] Audio track recovered successfully.");
        } catch (e) {
          console.error("[AudioHealth] Recovery failed:", e);
        }
      }

      // Sender-completeness check: connectionState/iceConnectionState stay
      // "connected" even when a peer is simply missing a sender for a track we
      // have locally (e.g. its initial offer/answer raced ahead of getUserMedia
      // and the later renegotiation was somehow missed). Neither this interval's
      // dead-track check above nor startPeerHealthCheck's connection-state check
      // would ever catch that, so close the gap here directly.
      const localTracks = localStream.current.getTracks();
      Object.values(peers.current).forEach((peer) => {
        if (!peer || peer.destroyed) return;
        try {
          const senders = peer._pc?.getSenders() || [];
          localTracks.forEach((track) => {
            const hasSender = senders.some((s) => s.track?.kind === track.kind);
            if (!hasSender) {
              console.warn(
                "[AudioHealth] Peer missing",
                track.kind,
                "sender, adding:",
              );
              peer.addTrack?.(track, localStream.current);
            }
          });
        } catch (e) {
          console.warn("[AudioHealth] sender-completeness check failed for peer:", e);
        }
      });
    }, 12000);
  }, []); // eslint-disable-line

  const stopAudioHealthCheck = () => {
    if (audioHealthTimerRef.current) {
      clearInterval(audioHealthTimerRef.current);
      audioHealthTimerRef.current = null;
    }
  };

  // Immediately re-cap video bitrate on all connected peers to match the current
  // network tier. Called when navigator.connection fires a "change" event so
  // healthy peers (which stay in "connected" state through a WiFi→cellular switch)
  // are also updated — the iceStateChange handler only fires at connect time.
  const reapplyAdaptiveBitrate = () => {
    const newBitrate = getAdaptiveBitrate();
    Object.values(peers.current).forEach((peer) => {
      if (!peer || peer.destroyed) return;
      try {
        const sender = peer._pc?.getSenders().find((s) => s.track?.kind === "video");
        if (sender) {
          const params = sender.getParameters();
          if (params.encodings?.length > 0) {
            params.encodings[0].maxBitrate = newBitrate;
            sender.setParameters(params).catch(() => {});
          }
        }
      } catch {}
    });
  };

  // ─── Stats-based bitrate adaptation ───────────────────────────────────────
  const bitrateAdaptTimerRef = useRef(null);

  const startBitrateAdaptation = useCallback(() => {
    if (bitrateAdaptTimerRef.current) clearInterval(bitrateAdaptTimerRef.current);

    // Per-peer rolling state — reset each time adaptation starts (e.g. on join)
    const peerState = {}; // userId → { bitrate, goodTicks, lastSent, lastLost }

    bitrateAdaptTimerRef.current = setInterval(async () => {
      for (const [userId, peer] of Object.entries(peers.current)) {
        if (!peer || peer.destroyed) continue;
        const pc = peer._pc;
        if (!pc) continue;
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (!sender) continue;

        try {
          const stats = await pc.getStats();
          let packetsSent = 0, packetsLost = 0, rtt = 0;

          stats.forEach((r) => {
            if (r.type === "outbound-rtp" && r.kind === "video") packetsSent = r.packetsSent || 0;
            if (r.type === "remote-inbound-rtp" && r.kind === "video") {
              packetsLost = r.packetsLost || 0;
              rtt = r.roundTripTime || 0; // seconds
            }
          });

          const prev = peerState[userId] || {
            bitrate: getAdaptiveBitrate(),
            goodTicks: 0,
            lastSent: packetsSent,
            lastLost: packetsLost,
          };

          const deltaSent = packetsSent - prev.lastSent;
          const deltaLost = Math.max(0, packetsLost - prev.lastLost);
          const lossRatio = deltaSent > 0 ? deltaLost / deltaSent : 0;

          let { bitrate, goodTicks } = prev;

          if (lossRatio > 0.05 || rtt > 0.25) {
            // Network struggling — step down 30%, floor at 150 kbps
            bitrate = Math.max(150000, Math.floor(bitrate * 0.7));
            goodTicks = 0;
          } else if (lossRatio < 0.01 && rtt < 0.15) {
            goodTicks += 1;
            if (goodTicks >= 5) {
              // 5 consecutive clean ticks — step up 20%, ceil at network tier max
              bitrate = Math.min(getAdaptiveBitrate(), Math.floor(bitrate * 1.2));
              goodTicks = 0;
            }
          } else {
            goodTicks = 0;
          }

          peerState[userId] = { bitrate, goodTicks, lastSent: packetsSent, lastLost: packetsLost };

          if (bitrate !== prev.bitrate) {
            const params = sender.getParameters();
            if (params.encodings?.length > 0) {
              params.encodings[0].maxBitrate = bitrate;
              sender.setParameters(params).catch(() => {});
            }
          }
        } catch { /* getStats can throw on destroyed PCs — ignore */ }
      }
    }, 4000);
  }, []); // eslint-disable-line

  const stopBitrateAdaptation = () => {
    if (bitrateAdaptTimerRef.current) {
      clearInterval(bitrateAdaptTimerRef.current);
      bitrateAdaptTimerRef.current = null;
    }
  };

  // ─── Peer health monitor ───────────────────────────────────────────────────
  // Every 15s, scan all expected peers. If any are gone or in a bad state,
  // recreate them. This catches silent drops that fire no events at all.
  const peerHealthTimerRef = useRef(null);

  const startPeerHealthCheck = useCallback(() => {
    if (peerHealthTimerRef.current) clearInterval(peerHealthTimerRef.current);
    peerHealthTimerRef.current = setInterval(() => {
      if (!socketRef.current?.connected) return;
      participantOfMeetingRef.current.forEach((user) => {
        if (!user.isJoin || !user.userId) return;
        const peer = peers.current[user.userId];
        const connState = peer?._pc?.connectionState;
        const iceState = peer?._pc?.iceConnectionState;
        const isDead =
          peer &&
          (connState === "failed" ||
            connState === "closed" ||
            iceState === "failed");
        if (isDead) {
          console.warn(
            "[PeerHealth] dead/missing peer for",
            user.userId,
            "— recreating",
          );
          recreatePeer({
            userId: user.userId,
            username: user.username || user.userNameValue || "",
            participant: participantOfMeetingRef.current,
          });
        }
      });
    }, 15000);
  }, []); // eslint-disable-line

  const stopPeerHealthCheck = () => {
    if (peerHealthTimerRef.current) {
      clearInterval(peerHealthTimerRef.current);
      peerHealthTimerRef.current = null;
    }
  };

  // Start audio health check when in meeting room
  useEffect(() => {
    if (isInMeetingRoom) {
      startAudioHealthCheck();
      startPeerHealthCheck();
      startBitrateAdaptation();
    } else {
      stopAudioHealthCheck();
      stopPeerHealthCheck();
      stopBitrateAdaptation();
    }
    return () => {
      stopAudioHealthCheck();
      stopPeerHealthCheck();
      stopBitrateAdaptation();
    };
  }, [isInMeetingRoom, startAudioHealthCheck, startPeerHealthCheck, startBitrateAdaptation]);
  // ───────────────────────────────────────────────────────────────────────────

  const stopMedia = () => {
    if (localStream.current) {
      localStream.current.getTracks().forEach((t) => t.stop());
      localStream.current = null;
      cameraVideoTrack.current = null;
      screenVideoTrack.current = null;
    }
  };

  // ---------- peer helpers ----------
  const addLocalTracksToPeer = (peer) => {
    if (!localStream.current) return;
    localStream.current.getTracks().forEach((track) => {
      try {
        peer.addTrack(track, localStream.current);
      } catch {}
    });
  };

  const destroyPeer = (userId) => {
    const p = peers.current[userId];
    if (!p) return;
    try {
      p.removeAllListeners && p.removeAllListeners();
    } catch {}
    try {
      p.destroy();
    } catch {}
    delete peers.current[userId];
  };

  const recreatePeer = (peerUser) => {
    const uid = peerUser.userId;
    const now = Date.now();
    const last = peerRecreateTime.current[uid] || 0;

    if (now - last < 6000) {
      console.warn("[Peer] Recreate suppressed (cooldown) for", uid);
      return;
    }
    peerRecreateTime.current[uid] = now;

    // Deterministic initiator: whichever side has the lexicographically lower
    // socket ID becomes initiator. This ensures exactly ONE side sends an offer
    // and the other waits — preventing offer/offer collision when both sides
    // detect failure simultaneously.
    const myId = socketRef.current?.id || "";
    const shouldInitiate = myId < uid;
    console.info("[Peer] Recreating", uid, "— initiator:", shouldInitiate);

    destroyPeer(uid);
    peers.current[uid] = createPeer(peerUser, shouldInitiate);
  };

  // Create peer with resilient wiring
  const createPeer = (userIdX, initiator) => {
    if (!iceServersRef.current) {
      console.warn("ICE servers not ready yet");
      return null;
    }

    const userId = userIdX.userId;
    const peerGuid = userIdX.guid || "";
    const userNameValue = userIdX.username || "";
    const participant =
      userIdX?.participant?.length > 0 ? userIdX?.participant : [];

    const peer = new window.SimplePeer({
      initiator,
      trickle: true,
      config: { iceServers: iceServersRef.current, iceTransportPolicy: "all" },
      // Don't pass stream here; add tracks for easier replaceTrack later
    });

    peer.__meta = { isScreenSharing: false, username: userNameValue };
    peer.isScreenSharing = false;
    peer.stream = localStream.current;

    // Add local tracks ASAP
    addLocalTracksToPeer(peer);

    // Signal up to SFU/signaling
    peer.on("signal", (data) => {
      if (!socketRef.current) return;
      socketRef.current.emit("signal", {
        userId,
        newUserName: userNameRef.current, // use stable ref, not stale closure
        room_id: room_idRef.current,
        signal: data,
      });
    });

    // Remote tracks assembled into a stream by SimplePeer
    peer.on("stream", (remoteStream) => {
      const isScreenShare =
        peers.current[userId]?.__meta?.isScreenSharing ||
        peers.current[userId]?.isScreenSharing;
      if (isScreenShare) {
        setIsScreenSharedInMeeting((prev) => ({ ...prev, isShared: true }));
        setScreenSharedStreams({
          userId,
          remoteStream,
          userNameValue,
          isScreenShare: true,
        });
        return;
      }

      setParticipantOfMeeting((prev) =>
        prev.map((user) => {
          // Match by userId (socket ID) OR by guid (stable — handles reconnect/stale userId)
          const isMatch =
            user.userId === userId || (peerGuid && user.guid === peerGuid);

          if (isMatch) {
            const fullName =
              [user.fname || user.firstName, user.lname || user.lastName]
                .filter(Boolean)
                .join(" ") ||
              user.username ||
              "";

            const updated = {
              ...user,
              userId, // sync socket ID in case it was stale
              isJoin: true,
              remoteStream,
              userNameValue: user.userNameValue || fullName,
              isScreenShare: false,
              // Read from user state directly — onUserJoined already set these correctly
              // from server data. Do NOT read from stale `participant` closure.
              isCameraOn: user.isCameraOn ?? true,
              isAudioOn: user.isAudioOn ?? true,
            };

            return updated;
          }
          return user;
        }),
      );
    });

    peer.on("iceStateChange", () => {
      const pc = peer._pc;
      if (!pc) return;
      const state = pc.iceConnectionState;
      console.log("[ICE]", userId, "=>", state);

      if (state === "connected" || state === "completed") {
        // Fix 2: cap video bitrate to the current network tier
        try {
          const sender = pc.getSenders().find((s) => s.track?.kind === "video");
          if (sender) {
            const params = sender.getParameters();
            if (params.encodings?.length > 0) {
              params.encodings[0].maxBitrate = getAdaptiveBitrate();
              sender.setParameters(params).catch(() => {});
            }
          }
        } catch {}
        // Fix 4: mark connection healthy
        setParticipantOfMeeting((prev) =>
          prev.map((user) => {
            const isMatch =
              user.userId === userId || (peerGuid && user.guid === peerGuid);
            return isMatch ? { ...user, connectionQuality: "good" } : user;
          }),
        );
      }

      if (state === "failed" || state === "disconnected") {
        // Fix 4: mark connection degraded
        setParticipantOfMeeting((prev) =>
          prev.map((user) => {
            const isMatch =
              user.userId === userId || (peerGuid && user.guid === peerGuid);
            return isMatch ? { ...user, connectionQuality: "poor" } : user;
          }),
        );
      }

      if (state === "failed") {
        console.warn("[ICE] failed — recreating peer", userId);
        recreatePeer({ userId, username: userNameValue, participant });
      } else if (state === "disconnected") {
        const disconnectedAt = Date.now();
        setTimeout(() => {
          // If failed already fired and triggered recreatePeer, peerRecreateTime
          // will be >= disconnectedAt — skip to avoid a second recreation.
          const lastRecreate = peerRecreateTime.current[userId] || 0;
          if (lastRecreate >= disconnectedAt) {
            console.info(
              "[ICE] disconnected timer: peer already recreated, skipping",
            );
            return;
          }
          const currentState = peers.current[userId]?._pc?.iceConnectionState;
          if (currentState === "disconnected" || currentState === "failed") {
            console.warn(
              "[ICE] disconnected did not recover, recreating peer",
              userId,
            );
            recreatePeer({ userId, username: userNameValue, participant });
          }
        }, 6000);
      }
    });

    // The connectionstatechange double-fires in Firefox and causes a second recreatePeer that breaks the recovery.
    // if (peer._pc) {
    //   peer._pc.addEventListener("connectionstatechange", () => {
    //     const connState = peer._pc?.connectionState;
    //     console.log("[PeerConn]", userId, "=>", connState);
    //     if (connState === "failed" || connState === "disconnected") {
    //       const waitMs = connState === "disconnected" ? 5000 : 2000;
    //       setTimeout(() => {
    //         const current = peers.current[userId]?._pc?.connectionState;
    //         // If still bad (or peer gone), recreate
    //         if (
    //           !current ||
    //           current === "failed" ||
    //           current === "disconnected"
    //         ) {
    //           console.warn(
    //             "[PeerConn]",
    //             connState,
    //             "— recreating peer",
    //             userId,
    //           );
    //           recreatePeer({ userId, username: userNameValue, participant });
    //         }
    //       }, waitMs);
    //     }
    //   });
    // }

    peer.on("close", () => {
      console.log("[Peer] close for", userId);
      destroyPeer(userId);
      if (userId === socketRef.current?.id) return;
      setTimeout(() => {
        // A legitimate leave/refresh (SocketInit's onUserLeft) also destroys the
        // peer, which fires this same close handler. Only recreate for a close
        // we didn't expect, i.e. the userId is still a live participant, not one
        // who has since left the room.
        const stillAParticipant = participantOfMeetingRef.current.some(
          (p) => p.userId === userId,
        );
        if (
          stillAParticipant &&
          !peers.current[userId] &&
          socketRef.current?.connected
        ) {
          console.info("[Peer] recreating after unexpected close:", userId);
          recreatePeer({ userId, username: userNameValue, participant });
        }
      }, 2000);
    });

    peer.on("error", (err) => {
      console.error("Peer error:", err);

      // Ignore user-initiated abort/close noise
      if (
        err?.name === "OperationError" &&
        /Abort|Close called/i.test(err.message || "")
      ) {
        return;
      }

      // Ignore duplicate-answer / wrong-state SDP errors — these are signaling
      // race conditions, not real failures. Recreating makes them worse.
      if (
        err?.name === "InvalidStateError" &&
        /setRemoteDescription|setLocalDescription/i.test(err.message || "")
      ) {
        return;
      }

      // Ignore "Connection failed" errors — those are already handled by
      // iceStateChange and connectionstatechange with proper cooldowns.
      // Recreating here on top of those creates the infinite loop.
      if (/Connection failed/i.test(err.message || "")) {
        return;
      }

      // Recreate for all other genuine errors (e.g. media errors)
      recreatePeer({ userId, username: userNameValue, participant });
    });

    return peer;
  };

  useEffect(() => {
    const handleNetworkRestore = () => {
      console.info("[Network] Network changed - recreating broken peers");
      if (!socketRef.current?.connected) return;
      Object.keys(peers.current).forEach((userId) => {
        const peer = peers.current[userId];
        const state = peer?._pc?.iceConnectionState;
        if (state && state !== "connected" && state !== "completed") {
          console.info(
            "[Network] Recreating peer for",
            userId,
            "state:",
            state,
          );
          const meta = peer?.__meta;
          recreatePeer({
            userId,
            username: meta?.username || "",
            participant: participantOfMeetingRef.current,
          });
        }
      });
    };

    const handleOnline = () => {
      console.info("[Network] online event — waiting 3s then checking peers");
      setTimeout(handleNetworkRestore, 3000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.info("[Network] Tab/screen visible — checking peers");
        setTimeout(handleNetworkRestore, 2000);
      }
    };

    // navigator.connection fires on Chrome for WiFi↔mobile switches.
    // Not available in Firefox or Safari — guard required.
    const handleConnectionChange = () => {
      console.info("[Network] navigator.connection change");
      // Re-cap bitrate immediately on all peers (including healthy ones that stay
      // "connected" through a network switch and never re-enter iceStateChange).
      reapplyAdaptiveBitrate();
      setTimeout(handleNetworkRestore, 3000);
    };

    // ── Socket reconnect — re-join room ────────────────────────────────────
    // When the network switches (WiFi → hotspot, WiFi → mobile data, power
    // outage + reconnect), socket.io auto-reconnects the socket transport.

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (navigator.connection) {
      navigator.connection.addEventListener("change", handleConnectionChange);
    }

    return () => {
      // socket reconnect listener lives in attachSocket — no cleanup needed
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (navigator.connection) {
        navigator.connection.removeEventListener(
          "change",
          handleConnectionChange,
        );
      }
    };
  }, []); // eslint-disable-line

  // ---------- mute/unmute & cam on/off ----------
  const isInMeetingRoomRef = useRef(false);
  useEffect(() => {
    isInMeetingRoomRef.current = isInMeetingRoom;
  }, [isInMeetingRoom]);

  const toggleVideo = () => {
    if (!localStream.current) return;
    const newVideoEnabled = !userOwnDataRef.current.isCameraOn;

    // ── WHY WE NEVER CALL track.enabled = false ──────────────────────────────
    // Chrome (desktop + mobile) suspends the ENTIRE getUserMedia capture session
    // when any track from that session has .enabled set to false. This silences
    // the audio track at the hardware/OS capture level — no JS can rescue it.
    //
    // The local display already handles camera-off correctly via isCameraOn
    // state: CameraFeed adds CSS d-none to the <video> element and shows
    // ParticipantCameraOffBox instead. No track manipulation needed for UI.
    //
    // For PEERS: we replace the peer's video sender with a 2×2 black canvas
    // track (camera off) or restore it to the real track (camera on). The
    // getUserMedia stream is never touched, so audio keeps flowing.
    // ─────────────────────────────────────────────────────────────────────────

    if (!newVideoEnabled) {
      // Camera OFF — send black video to peers, keep audio untouched
      const canvas = Object.assign(document.createElement("canvas"), {
        width: 2,
        height: 2,
      });
      canvas.getContext("2d").fillRect(0, 0, 2, 2);
      const blackTrack = canvas.captureStream(1).getVideoTracks()[0];
      Object.values(peers.current).forEach((peer) => {
        try {
          const vs = peer._pc
            ?.getSenders()
            .find((s) => s.track?.kind === "video");
          if (vs) vs.replaceTrack(blackTrack);
        } catch {}
      });
    } else {
      // Camera ON — restore real video track to peers
      const realTrack =
        cameraVideoTrack.current ||
        rawCameraStream.current?.getVideoTracks()[0] ||
        localStream.current.getVideoTracks()[0];
      if (realTrack) {
        Object.values(peers.current).forEach((peer) => {
          try {
            const vs = peer._pc
              ?.getSenders()
              .find((s) => s.track?.kind === "video");
            if (vs) vs.replaceTrack(realTrack);
          } catch {}
        });
      }
    }

    setUserOwnData((prev) => ({ ...prev, isCameraOn: newVideoEnabled }));

    console.log("[toggleVideo] emitting camera_signal", {
      userId: userOwnDataRef.current.userId,
      guid: userOwnDataRef.current.guid,
      room_id: room_idRef.current,
      isCameraOn: newVideoEnabled,
    });

    if (socketRef.current && userOwnDataRef.current.guid) {
      socketRef.current.emit("camera_signal", {
        userId: userOwnDataRef.current.userId,
        guid: userOwnDataRef.current.guid,
        room_id: room_idRef.current,
        isCameraOn: newVideoEnabled,
      });
    }
  };

  const toggleAudio = () => {
    if (!localStream.current) return;
    // Use ref — not audioEnabled state — to avoid stale closure after re-renders
    const newAudioEnabled = !audioEnabledRef.current;
    audioEnabledRef.current = newAudioEnabled;

    // Apply to local stream audio tracks
    localStream.current
      .getAudioTracks()
      .forEach((t) => (t.enabled = newAudioEnabled));

    // Also apply to rawCameraStream in case they diverged (effectsProcessor active)
    rawCameraStream.current
      ?.getAudioTracks()
      .forEach((t) => (t.enabled = newAudioEnabled));

    // Ensure every peer audio sender has a live track pointing to the correct state.
    // This is the critical fix: if localStream was rebuilt (e.g. by effectsProcessor),
    // the peer sender might be pointing to a detached or audio-less track.
    const liveAudioTrack =
      rawCameraStream.current?.getAudioTracks()[0] ||
      localStream.current.getAudioTracks()[0];
    if (liveAudioTrack) {
      liveAudioTrack.enabled = newAudioEnabled;
      Object.values(peers.current).forEach((peer) => {
        try {
          const sender = peer._pc
            ?.getSenders()
            .find((s) => s.track?.kind === "audio");
          if (sender && sender.track !== liveAudioTrack) {
            sender.replaceTrack(liveAudioTrack);
          }
        } catch {}
      });
    }

    setAudioEnabled(newAudioEnabled);
    setUserOwnData((prev) => ({ ...prev, isAudioOn: newAudioEnabled }));

    console.log("[toggleAudio] emitting audio_signal", {
      userId: userOwnDataRef.current.userId,
      guid: userOwnDataRef.current.guid,
      room_id: room_idRef.current,
      isAudioOn: newAudioEnabled,
    });

    if (socketRef.current && userOwnDataRef.current.guid) {
      socketRef.current.emit("audio_signal", {
        userId: userOwnDataRef.current.userId,
        guid: userOwnDataRef.current.guid,
        room_id: room_idRef.current,
        isAudioOn: newAudioEnabled,
      });
    }
  };

  // ---------- screen share ----------
  const shareScreen = async () => {
    try {
      // Get the screen stream — cap resolution so low-bandwidth candidates
      // are not saturated by a full-resolution screen capture.
      screenStreamRef.current = await navigator.mediaDevices.getDisplayMedia({
        video: { width: { max: 1280 }, height: { max: 720 }, frameRate: { max: 15 } },
      });

      // Check there is at least one connected peer to share to
      if (Object.keys(peers.current).length === 0) {
        console.error("No peers connected to share screen with");
        return;
      }

      // Add the screen stream to the peer connections for other users
      for (let userId in peers.current) {
        if (Object.prototype.hasOwnProperty.call(peers.current, userId)) {
          try {
            peers.current[userId].addStream(screenStreamRef.current);
            peers.current[userId].isScreenSharing = true; // Set the flag when screen sharing starts
          } catch (e) {
            console.error("Error adding screen stream to peer", userId, e);
          }
        }
      }

      // Notify other peers about screen sharing
      socketRef.current?.emit("share_screen", {
        room_id,
        userId: socketRef.current.id,
        isScreenSharing: true,
      });

      // Display the screen share locally
      setIsScreenSharedInMeeting({
        isShared: true,
        isSelfShared: true,
      });
      setScreenSharedStreams({
        userId: socketRef.current.id,
        remoteStream: screenStreamRef.current,
        userNameValue: userOwnData.username,
        isScreenShare: true,
      });

      // When user stops via browser UI "Stop sharing"
      const [track] = screenStreamRef.current.getVideoTracks();
      if (track) {
        track.onended = () => {
          stopSharedScreenHandler();
        };
      }
    } catch (error) {
      console.error("Error sharing screen:", error);
    }
  };

  const stopSharedScreenHandler = async () => {
    if (screenSharedStreams.remoteStream) {
      try {
        // Stop each track of the screen sharing stream
        screenSharedStreams.remoteStream
          .getTracks()
          .forEach((track) => track.stop());
      } catch (e) {
        console.error("Error stopping screen share tracks", e);
      }

      setIsScreenSharedInMeeting({
        isShared: false,
        isSelfShared: false,
      });

      setScreenSharedStreams({
        userId: socketRef.current?.id,
        remoteStream: null,
        userNameValue: userOwnData.username,
        isScreenShare: false,
      });

      socketRef.current?.emit("share_screen", {
        room_id,
        userId: socketRef.current.id,
        isScreenSharing: false,
      });

      socketRef.current?.emit("stop_share_screen", {
        room_id,
        userId: socketRef.current.id,
      });
    }
  };

  const connectNewUserInScreenSharing = () => {
    if (!screenSharedStreams.remoteStream || !currentUserJoined.userId) return;
    const peer = peers.current[currentUserJoined.userId];
    if (!peer) return;
    try {
      peer.addStream(screenSharedStreams.remoteStream);
      peer.isScreenSharing = true;
    } catch (e) {
      console.error("Error adding screen stream for newly joined user", e);
    }
  };

  // ---------- recording ----------
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // const startRecording = async () => {
  //   const screenStream = await navigator.mediaDevices.getDisplayMedia({
  //     video: true,
  //     audio: true,
  //   });
  //   const micStream = await navigator.mediaDevices.getUserMedia({
  //     audio: true,
  //   });

  //   const combinedStream = new MediaStream([
  //     ...screenStream.getVideoTracks(),
  //     ...screenStream.getAudioTracks(),
  //     ...micStream.getAudioTracks(),
  //   ]);

  //   const recorder = new MediaRecorder(combinedStream, {
  //     mimeType: "video/webm",
  //   });
  //   setMediaRecorder(recorder);

  //   let recordedChunks = [];

  //   recorder.ondataavailable = (event) => {
  //     if (event.data.size > 0) {
  //       recordedChunks.push(event.data);
  //       socketRef.current?.emit("upload_chunk", {
  //         room_id,
  //         chunk: event.data,
  //       });
  //     }
  //   };

  //   recorder.onstop = () => {
  //     if (recordedChunks.length > 0) {
  //       const blob = new Blob(recordedChunks, { type: "video/webm" });
  //       const url = URL.createObjectURL(blob);
  //       console.log("Recording saved:", url);
  //     }
  //     recordedChunks = [];
  //   };

  //   recorder.start(2500);
  //   setIsRecording(true);
  // };

  const startRecording = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext("2d");

    // ── Audio mixing ────────────────────────────────────────────────────────
    // AudioContext mixes all audio tracks into one — addTrack() with multiple
    // audio sources is unreliable across browsers.
    const audioCtx = new AudioContext();
    const audioDestination = audioCtx.createMediaStreamDestination();

    const connectAudio = (stream) => {
      if (!stream) return;
      if (stream.getAudioTracks().length === 0) return;
      try {
        audioCtx.createMediaStreamSource(stream).connect(audioDestination);
      } catch {}
    };

    // Local mic
    connectAudio(localStream.current);

    // All remote participants
    participantOfMeetingRef.current.forEach((u) => {
      if (u.remoteStream) connectAudio(u.remoteStream);
    });

    // ── Hidden video elements per stream ────────────────────────────────────
    // Build in-memory <video> nodes attached to each stream so we can drawImage
    // them on canvas regardless of DOM visibility or CSS state.
    const buildVideoEl = (stream) => {
      const v = document.createElement("video");
      v.srcObject = stream;
      v.autoplay = true;
      v.playsInline = true;
      v.muted = true;
      v.play().catch(() => {});
      return v;
    };

    // ── Canvas draw loop ────────────────────────────────────────────────────
    const drawFrame = () => {
      ctx.fillStyle = "#080d18";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Collect current streams: screen share takes main area, others are tiles
      const screenStream = screenSharedStreams?.remoteStream || null;
      const participantStreams = participantOfMeetingRef.current
        .filter((u) => u.isJoin && u.remoteStream && !u.isScreenShare)
        .map((u) => u.remoteStream);

      // Own local stream
      const ownStream = localStream.current;

      if (screenStream) {
        // Screen share layout: screen takes left 75%, tiles stack right 25%
        const screenVid =
          canvasRecorderRef.current.streamVideoMap.get(screenStream) ||
          (() => {
            const v = buildVideoEl(screenStream);
            canvasRecorderRef.current.streamVideoMap.set(screenStream, v);
            return v;
          })();

        ctx.drawImage(screenVid, 0, 0, canvas.width * 0.75, canvas.height);

        const sideStreams = [...participantStreams, ownStream].filter(Boolean);
        const tileH = canvas.height / (sideStreams.length || 1);
        const tileX = canvas.width * 0.75;
        const tileW = canvas.width * 0.25;

        sideStreams.forEach((stream, i) => {
          const v =
            canvasRecorderRef.current.streamVideoMap.get(stream) ||
            (() => {
              const vid = buildVideoEl(stream);
              canvasRecorderRef.current.streamVideoMap.set(stream, vid);
              return vid;
            })();
          ctx.drawImage(v, tileX, i * tileH, tileW, tileH);
        });
      } else {
        // Normal grid layout
        const allStreams = [...participantStreams, ownStream].filter(Boolean);
        if (allStreams.length === 0) {
          canvasRecorderRef.current.animFrame =
            requestAnimationFrame(drawFrame);
          return;
        }

        const cols = Math.ceil(Math.sqrt(allStreams.length));
        const rows = Math.ceil(allStreams.length / cols);
        const tileW = canvas.width / cols;
        const tileH = canvas.height / rows;

        allStreams.forEach((stream, i) => {
          const v =
            canvasRecorderRef.current.streamVideoMap.get(stream) ||
            (() => {
              const vid = buildVideoEl(stream);
              canvasRecorderRef.current.streamVideoMap.set(stream, vid);
              return vid;
            })();
          const col = i % cols;
          const row = Math.floor(i / cols);
          try {
            ctx.drawImage(v, col * tileW, row * tileH, tileW, tileH);
          } catch {}
        });
      }

      canvasRecorderRef.current.animFrame = requestAnimationFrame(drawFrame);
    };

    // ── Combine canvas + mixed audio ────────────────────────────────────────
    const canvasStream = canvas.captureStream(30);
    audioDestination.stream
      .getAudioTracks()
      .forEach((t) => canvasStream.addTrack(t));

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";

    const recorder = new MediaRecorder(canvasStream, { mimeType });
    setMediaRecorder(recorder);

    let recordedChunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.push(event.data);
        socketRef.current?.emit("upload_chunk", { room_id, chunk: event.data });
      }
    };

    recorder.onstop = () => {
      if (recordedChunks.length > 0) {
        const blob = new Blob(recordedChunks, { type: "video/webm" });
        console.log("Recording saved:", URL.createObjectURL(blob));
      }
      recordedChunks = [];
      cancelAnimationFrame(canvasRecorderRef.current?.animFrame);
      // Clean up hidden video elements
      canvasRecorderRef.current?.streamVideoMap?.forEach((v) => {
        v.srcObject = null;
      });
      audioCtx.close();
    };

    canvasRecorderRef.current = {
      canvas,
      ctx,
      animFrame: null,
      stream: canvasStream,
      audioCtx,
      streamVideoMap: new Map(), // stream → hidden <video> el
    };

    drawFrame();
    recorder.start(2500);
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      socketRef.current?.emit("stop_recording", { room_id });
    }
  };

  // ---------- leave interview ----------
  function leaveInterview() {
    try {
      socketRef.current?.emit("leave", {
        username: userName,
        userId: userOwnData.userId,
        room_id,
        guid: userOwnData.guid,
      });
    } catch {}
    setIsInterviewEnded(true);

    // teardown peers + media
    Object.keys(peers.current).forEach(destroyPeer);
    stopMedia();
    stopAudioHealthCheck();
    stopPeerHealthCheck();
    stopBitrateAdaptation();
    effectsProcessor.destroy();
    safeRemoveAllSocketListeners();
  }

  // ---------- late join screen sync ----------
  useEffect(() => {
    if (isScreenSharedInMeeting.isSelfShared) {
      const timer = setTimeout(() => {
        connectNewUserInScreenSharing();
        socketRef.current?.emit("sharedScreenDataRequest", {
          room_id,
          userId: socketRef.current?.id,
          send_to: currentUserJoined.userId,
          isScreenSharing: true,
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [currentUserJoined.userId]);

  // ---------- signaling & room flow ----------

  useEffect(() => {
    if (!(isInMeetingRoom && socketRef.current)) return;

    // ---- user_joined ----
    const onUserJoined = (data) => {
      if (!data?.newUser || !Array.isArray(data?.participantData)) {
        console.warn("[onUserJoined] malformed event, ignoring", data);
        return;
      }
      setCurrentUserJoined(data.newUser);

      // Compute updated participants synchronously from the ref (best available state).
      // We do this OUTSIDE the setState callback so we can also pass the result
      // to createPeer — the old code used the stale ref which caused isCameraOn/isAudioOn
      // to be undefined in the peer stream handler, making tiles render blank.
      const updatedParticipants = participantOfMeetingRef.current.map(
        (user) => {
          const matchingUser = data.participantData.find(
            (join) => join.guid === user.guid,
          );
          if (!matchingUser) return user;
          return enrichUserWithNameValue({
            ...user,
            ...matchingUser,
            isJoin: true,
            // Preserve existing camera/audio state if already set by a signal —
            // participantData carries join-time values which may be stale
            isCameraOn: matchingUser.isCameraOn ?? true,
            isAudioOn: matchingUser.isAudioOn ?? true,
          });
        },
      );

      // Update the ref immediately so createPeer (called below) gets fresh data
      participantOfMeetingRef.current = updatedParticipants;
      setParticipantOfMeeting(updatedParticipants);

      // Play a join tone
      if (
        typeof document !== "undefined" &&
        joinSoundRef.current &&
        String(data.newUser.userId) !== String(userOwnData.userId)
      ) {
        try {
          joinSoundRef.current.currentTime = 0;
          joinSoundRef.current.play();
        } catch (e) {
          console.warn("Could not play join sound", e);
        }
      }

      // Save own data only once (fixed to use ref)
      if (!isSavedCurrentUserData.current) {
        const enrichedNewUser = enrichUserWithNameValue(data.newUser);
        // eslint-disable-next-line
        userData = enrichedNewUser;
        setUserOwnData((preData) => ({ ...preData, ...enrichedNewUser }));
        isSavedCurrentUserData.current = true;

        // eslint-disable-next-line
        participantData = data.participantData.filter(
          (participant) => participant.userId !== data.newUser.userId,
        );
        setParticipantList(participantData.map(enrichUserWithNameValue));
      } else {
        participantData = data.participantData.filter(
          (participant) => participant.userId !== userData.userId,
        );
        setParticipantList(participantData.map(enrichUserWithNameValue));
      }

      // Pass updatedParticipants (freshly computed above) — not the stale ref
      if (data.newUser.userId !== socketRef.current.id) {
        peers.current[data.newUser.userId] = createPeer(
          { ...data.newUser, participant: updatedParticipants },
          true,
        );
      }

      // Scenario B: SOMEONE ELSE was sharing screen and our network switched.
      // Their share_screen signal fired before our disconnect — the new peer has
      // no screen stream. Ask them to re-add it to the new peer connection.
      if (data.newUser.isScreenSharing) {
        setTimeout(() => {
          socketRef.current?.emit("sharedScreenDataRequest", {
            room_id: room_idRef.current,
            userId: socketRef.current?.id,
            send_to: data.newUser.userId,
            isScreenSharing: true,
          });
          console.info(
            "[onUserJoined] requested screen stream from",
            data.newUser.userId,
          );
        }, 2000);
      }
    };

    socketRef.current.on("user_joined", onUserJoined);
    unsubscribers.current.push(() =>
      socketRef.current.off("user_joined", onUserJoined),
    );

    // ---- signal exchange ----
    const onSignal = (data) => {
      const targetUserId = data.userId;
      const remoteName = data.username;

      if (targetUserId === socketRef.current.id) return;

      // Use ref for current participant list — no stale closure
      const currentParticipants = participantOfMeetingRef.current;

      // If we get an offer and no peer exists, create as non-initiator
      if (!peers.current[targetUserId] && data.signal?.type === "offer") {
        peers.current[targetUserId] = createPeer(
          {
            userId: targetUserId,
            username: remoteName,
            participant: currentParticipants,
          },
          false,
        );
      }

      const p = peers.current[targetUserId];
      if (p && !p.destroyed) {
        try {
          const pc = p._pc;
          if (
            pc &&
            pc.signalingState === "stable" &&
            data.signal?.type === "answer"
          ) {
            console.warn(
              "Ignoring duplicate answer for peer",
              targetUserId,
              "in stable state",
            );
            return;
          }

          p.signal(data.signal);
        } catch (e) {
          console.warn("signal apply failed, maybe stale", e);
          if (
            !(
              e?.name === "InvalidStateError" &&
              /setRemoteDescription/i.test(e.message || "")
            )
          ) {
            recreatePeer({
              userId: targetUserId,
              username: remoteName,
              participant: currentParticipants,
            });
          }
        }
      }
    };
    socketRef.current.on("signal", onSignal);
    unsubscribers.current.push(() => socketRef.current.off("signal", onSignal));

    return () => {
      try {
        socketRef.current.off("user_joined", onUserJoined);
        socketRef.current.off("signal", onSignal);
      } catch {}
    };
    // eslint-disable-next-line
  }, [isInMeetingRoom]);
  // ───────────────────────────────────────────────────────────────────────────

  // Exposed so useVideoEffects hook can push new processed track to all peers
  const replaceVideoTrackForPeers = useCallback((newTrack) => {
    if (!localStream.current) return;
    // Always get audio from rawCameraStream — localStream.current might be a
    // processed (canvas) stream from effectsProcessor that has no audio tracks.
    // rawCameraStream always has the original mic track from getUserMedia.
    const audioTracks = rawCameraStream.current?.getAudioTracks().length
      ? rawCameraStream.current.getAudioTracks()
      : localStream.current.getAudioTracks();
    const newStream = new MediaStream([newTrack, ...audioTracks]);
    localStream.current = newStream;
    cameraVideoTrack.current = newTrack;
    // Tell CameraFeed to re-attach srcObject — fixes black video on effect switch
    window.dispatchEvent(
      new CustomEvent("bizro:videoTrackChanged", {
        detail: { stream: newStream },
      }),
    );
    // Push to every active peer sender, or add one if this peer never had a
    // video sender to begin with. Must use the SimplePeer wrapper's addTrack,
    // not peer._pc.addTrack, so a missing sender actually triggers renegotiation.
    Object.values(peers.current).forEach((peer) => {
      try {
        const sender = peer._pc
          ?.getSenders()
          .find((s) => s.track?.kind === "video");
        if (sender) {
          sender.replaceTrack(newTrack);
        } else {
          peer.addTrack?.(newTrack, newStream);
        }
      } catch {}
    });
  }, []);

  return (
    <CommonContext.Provider
      value={{
        userName,
        stopMedia,
        setUserName,
        userOwnData,
        startCameraAudio,
        socketRef,
        createPeer,
        localStream,
        room_id,
        isCameraOn,
        toggleAudio,
        audioEnabled,
        setAudioEnabled,
        toggleVideo,
        selectedCameraIdRef,
        selectedMicIdRef,
        participateIdToMessage,
        setParticipateIdToMessage,
        participantList,
        setParticipantList,
        groupMessagesData,
        addMessageToGroup: (m) => setGroupMessagesData((p) => [...p, m]),
        privateChatMessages,
        setPrivateChatMessages,
        shareScreen,
        isScreenSharedInMeeting,
        setIsScreenSharedInMeeting,
        screenSharedStreams,
        setScreenSharedStreams,
        stopSharedScreenHandler,
        leaveInterview,
        isInMeetingRoom,
        setIsInMeetingRoom,
        setRoom_id,
        isLoggedIn,
        participantOfMeeting,
        setUserOwnData,
        setIsLoggedIn,
        isInitialLoading,
        generalChatMessages,
        setGeneralChatMessages,
        isInterviewEnded,
        setIsInterviewEnded,
        typeOfInterview,
        setGroupMessagesData,
        setParticipantOfMeeting,
        setIsCameraOn,
        aiAgentDetail,
        peers,
        interviewDetails,
        isComeNewMessage,
        setIsComeNewMessage,
        isRecording,
        setIsRecording,
        startRecording,
        stopRecording,
        note,
        setNote,
        idRequestForNotes,
        feedbackGuid,
        setFeedbackGuid,
        microphoneError,
        setMicrophoneError,
        isValidUrl,
        backgroundEffect,
        setBackgroundEffect,
        replaceVideoTrackForPeers,
        rawCameraStream,
      }}
    >
      {children}
    </CommonContext.Provider>
  );
};
